import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import albumCover from '../assets/album-cover.png';
import bgImage from '../assets/bg-image.png';
import { supabase } from '../lib/supabase';

const VideoUploadPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const playerName = searchParams.get('player') || 'Player';
  
  const [videoTitle, setVideoTitle] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!videoTitle.trim() || !videoFile) return;
    
    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      // Upload video file to Supabase Storage
      const fileExt = videoFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `videos/${fileName}`;
      
      setUploadProgress(10);
      
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('game-videos')
        .upload(filePath, videoFile, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) throw uploadError;
      
      setUploadProgress(70);
      
      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('game-videos')
        .getPublicUrl(filePath);
      
      if (!urlData?.publicUrl) throw new Error('Failed to get video URL');
      
      setUploadProgress(90);
      
      // Save video metadata to database
      const { error: dbError } = await supabase
        .from('videos')
        .insert({
          title: videoTitle.trim(),
          player_name: playerName,
          video_url: urlData.publicUrl,
          thumbnail_url: null
        });
      
      if (dbError) throw dbError;
      
      setUploadProgress(100);
      setSubmitted(true);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload video. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <motion.div
          className="max-w-md w-full relative z-10 text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <motion.div
            className="text-8xl mb-6"
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            🎉
          </motion.div>
          <h1 className="text-4xl font-black text-white mb-4">
            <span className="text-cyan-400">VIDEO UPLOADED!</span>
          </h1>
          <p className="text-gray-300 text-lg mb-2">
            Thanks <strong className="text-amber-400">{playerName}</strong>!
          </p>
          <p className="text-gray-400 text-sm mb-8">
            Your gameplay video has been submitted for review. It will appear in the community gallery soon!
          </p>
          <div className="bg-white/10 rounded-2xl p-4 mb-6">
            <p className="text-white font-medium">"{videoTitle}"</p>
          </div>
          <button
            onClick={() => window.location.href = '/'}
            className="px-8 py-3 bg-cyan-500 hover:bg-cyan-400 rounded-xl text-black font-bold transition-colors"
          >
            🏠 Back to Game
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="absolute inset-0 bg-black/70" />
      <motion.div
        className="max-w-lg w-full relative z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            className="inline-block mb-4"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl blur opacity-60" />
              <img
                src={albumCover}
                alt="Album Cover"
                className="relative w-32 h-32 rounded-xl object-cover border-2 border-cyan-400"
              />
            </div>
          </motion.div>
          <h1 className="text-3xl font-black text-white mb-2">
            UPLOAD YOUR <span className="text-cyan-400">GAMEPLAY</span>
          </h1>
          <p className="text-gray-400">
            Hey <strong className="text-amber-400">{playerName}</strong>! Share your game night moments 🎬
          </p>
        </div>

        {/* Upload Form */}
        <div className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
          {/* Video Title */}
          <div className="mb-6">
            <label className="block text-white font-bold text-sm mb-2">📝 VIDEO TITLE</label>
            <input
              type="text"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              placeholder="e.g. Our Epic Game Night - Round 3..."
              className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400"
              maxLength={80}
            />
          </div>

          {/* Video Upload */}
          <div className="mb-6">
            <label className="block text-white font-bold text-sm mb-2">🎥 SELECT VIDEO</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {videoPreview ? (
              <div className="relative">
                <video
                  src={videoPreview}
                  className="w-full h-48 object-cover rounded-xl border border-white/20"
                  controls
                />
                <button
                  onClick={() => {
                    setVideoFile(null);
                    setVideoPreview(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 px-3 py-1 bg-red-500 hover:bg-red-400 rounded-lg text-white text-xs font-bold"
                >
                  ✕ Remove
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-white/30 rounded-xl flex flex-col items-center justify-center hover:border-cyan-400 hover:bg-white/5 transition-colors"
              >
                <span className="text-5xl mb-3">📱</span>
                <span className="text-white font-bold">Tap To Upload</span>
                <span className="text-gray-400 text-sm mt-1">Select a video from your gallery</span>
              </button>
            )}
          </div>

          {/* Submit */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          {isSubmitting && uploadProgress > 0 && (
            <div className="mb-4">
              <div className="bg-black/30 rounded-full h-2 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-gray-400 text-xs mt-1 text-center">
                {uploadProgress < 70 ? 'Uploading video...' : uploadProgress < 90 ? 'Saving to database...' : 'Almost done!'}
              </p>
            </div>
          )}
          
          <motion.button
            onClick={handleSubmit}
            disabled={!videoTitle.trim() || !videoFile || isSubmitting}
            className="w-full py-4 bg-gradient-to-r from-cyan-400 to-blue-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-xl text-black disabled:text-gray-400 font-bold text-lg transition-all"
            whileHover={videoTitle.trim() && videoFile ? { scale: 1.02 } : {}}
            whileTap={videoTitle.trim() && videoFile ? { scale: 0.98 } : {}}
          >
            {isSubmitting ? '⏳ UPLOADING...' : '🚀 UPLOAD VIDEO'}
          </motion.button>
        </div>

        {/* Back link */}
        <div className="text-center mt-6 space-y-3">
          <button
            onClick={() => window.location.href = '/'}
            className="text-gray-400 hover:text-cyan-400 text-sm transition-colors"
          >
            ← Back to Game
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default VideoUploadPage;
